import { AlertTriangle, Check, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useConsumeOrganisationSequence,
  useLocations,
  useOrganisation,
  useOrganisationRoutes,
  useParties,
  useProducts as useDirectoryProducts,
  useRoadLegs,
} from '@my/directory-api/hooks';
import type { Address, Party } from '@my/directory-api';
import {
  useCargoDefinitions,
  useCreateOrder,
  useOrder,
  useOrderBookings,
  useOrderDocuments,
  useOrderChargeCosts,
  useOrderEmissions,
  useOrderTasks,
  useReplaceOrderChargeCosts,
  useResequenceOrderTasks,
  useServiceTags,
  useUpdateOrder,
  useUpdateOrderTask,
  useWorkflowPolicies,
} from '@my/tms-api/hooks';
import {
  CARGO_TYPES,
  findVariantPreset,
  parsePropertyGroups,
  tmsApi,
  type CargoDefinition,
  type CargoTypeValue,
  type Invoice,
  type OrderTask,
  type ServiceTag,
} from '@my/tms-api';
import { useQueries } from '@tanstack/react-query';
import { ConfirmDialog, ErrorBanner, Loading, PageHeader, showToast } from '@my/ui';
import { useNavigate } from 'react-router';
// Aliased away from the global `Map` constructor — without the rename
// the page's `new Map<string, T>()` lookups (e.g. address index) would
// resolve to this React component instead of the JS built-in.
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import {
  extractOrderReferenceExtras,
  extractOrderReferenceValues,
  parseOrderReferenceLabels,
  parseRequiredOrderReferenceSlots,
  toOrderReferenceSavePayload,
  type OrderReferenceExtra,
} from '@/utils/orderReferenceLabels';
import { WORKFLOW_GROUPS } from '@/features/settings/workflows/lib/mockWorkflow';
import { applyPolicy } from '@/features/settings/workflows/lib/policyMapping';
import {
  allocationKeyOf,
  allocationMeasureFor,
  cargoItemProductLabel,
  collectedAmount,
  deliveredAmount,
  productLineLabel,
} from './lib/allocation';
import { buildCargoProductOptions, cargoAllowsMultipleProducts } from './lib/cargoProducts';
import { podCoverageFrom } from './lib/pod';
import { useOrderActions } from './lib/useOrderActions';
import { ORDER_STATUS_LABEL, ORDER_STATUS_TEXT } from './lib/orderStatus';
import {
  assessMove,
  correctionMovesFrom,
  movesFrom,
  toWorkflowSubject,
  type MoveAssessment,
} from './lib/orderWorkflow';
import { fmtInstant } from '@/utils/dates';
import { formatTempSpecRange } from './lib/format';
import type { TransportJob } from './lib/transportPricing';
import { StartFromPreviousOrder } from './components/StartFromPreviousOrder';
import { parseCopyFunctions } from './lib/copyFunctions';
import {
  buildCargoPick,
  CARGO_HANDLING_TASK_TYPES,
  CARGO_TASK_TYPES,
  cargoFamilyFor,
  cargoHandlingPhaseFor,
  parseCargoPick,
  VARIANT_EXPANDED_TYPES,
} from './lib/cargoPick';
import { adaptOrderBookingToMock, adaptOrderTaskToMock } from './lib/adapters';
import { buildResequenceItems, findChronologyConflicts, moveTask } from './lib/taskOrder';
import { ChargesAndCostsPanel, type ChargeCostSnapshot } from './components/ChargesAndCostsPanel';
import { InvoiceToControl } from './components/InvoiceToControl';
import { lineToInput } from './lib/charges';
import { DocumentsPanel } from './components/DocumentsPanel';
import { DispatchButton } from './components/DispatchButton';
import { DispatchPanel, type DispatchPanelView } from './components/DispatchPanel';
import { OrderMessagesButton } from './components/OrderMessagesButton';
import { OrderCheckpointsButton } from './components/OrderCheckpointsButton';
import { ReferencesPanel } from './components/ReferencesPanel';
import { CopyOrderButton } from './components/CopyOrderButton';
import { SaveAsTemplateButton } from './components/OrderTemplateDialogs';
import { OrderHistoryButton } from './components/OrderHistoryButton';
import { RouteInfoPanel } from './components/RouteInfoPanel';
import { buildOrderJourney, makeRoadResolver } from './lib/orderJourney';
import { resolveOrganisationRoutes } from './lib/route';
import { useCrossingCatalogue } from '../ferry/useCrossingCatalogue';
import { TaskCard } from './components/TaskCard';
import { inheritedTemperatureC, inheritedUnitIdentifiers } from './lib/taskDefaults';
import { TaskCreateForm } from './components/TaskCreateForm';
import {
  Combobox,
  HEADER_LABEL,
  HEADER_VALUE_ROW,
  HeaderInfo,
  InputWithSpecHint,
  NumberFieldInput,
} from './components/inputs';
import type {
  CargoFamily,
  CollectionItemOption,
  MockBooking,
  MockTask,
  PalletVariantOption,
  TaskPanelContext,
  TaskTemperatureSpec,
} from './lib/types';

// ── Page ─────────────────────────────────────────────────────────────────

export default function OrderDetailsPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id || id === 'new';
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const navigate = useNavigate();

  const createOrderMutation = useCreateOrder();
  const updateOrderMutation = useUpdateOrder();
  const replaceChargeCostsMutation = useReplaceOrderChargeCosts();

  // Latest charge/cost lines reported by the panel. Held in a ref (not state)
  // so per-keystroke edits don't re-render the page; read on footer Save.
  const chargeCostsRef = useRef<ChargeCostSnapshot>({ charges: [], costs: [], ready: false });
  const handleChargeCostsChange = useCallback((snapshot: ChargeCostSnapshot) => {
    chargeCostsRef.current = snapshot;
  }, []);

  const existingOrderQuery = useOrder(isNew ? null : (id ?? null));

  // Parties: customers for the header combobox; suppliers to resolve
  // booking.carrierPartyId to a display name on the chip.
  const customersQuery = useParties(orgId, 'Customer');
  const suppliersQuery = useParties(orgId, 'Supplier');

  const organisationQuery = useOrganisation(orgId);
  const orderReferenceLabels = useMemo(
    () => parseOrderReferenceLabels(organisationQuery.data?.tmsOrderReferenceLabelsJson),
    [organisationQuery.data?.tmsOrderReferenceLabelsJson],
  );

  // IsoTank/Container definitions expand to one option per variant so
  // operators can pick "20ft Container" directly.
  const cargoDefinitionsQuery = useCargoDefinitions(orgId);

  const orderTasksQuery = useOrderTasks(isNew ? null : (id ?? null));
  const orderBookingsQuery = useOrderBookings(isNew ? null : (id ?? null));
  const locationsQuery = useLocations(orgId);

  const addressById = useMemo(() => {
    const map = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) {
      map.set(a.id, a);
    }
    return map;
  }, [locationsQuery.data]);

  // Sorted client-side as well as server-side: the BE list endpoint
  // orders by `sequence`, but the resequence mutation's optimistic
  // update writes straight into this cache, so the panel can't assume
  // every array it renders came from a GET. Matches `OrdersPage`.
  const displayTasks = useMemo<MockTask[]>(() => {
    return (orderTasksQuery.data ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map((task) => adaptOrderTaskToMock(task, addressById.get(task.addressId)));
  }, [orderTasksQuery.data, addressById]);

  // Routes, for proposing a sailing on an international order that has no
  // booking yet: the organisation's picks resolved against the crossing list
  // (My.Ferry's routes plus the catalogue file). Long-lived caches the booking
  // form already warms, so this costs nothing on a second order.
  const crossingCatalogue = useCrossingCatalogue();
  const orgRoutesQuery = useOrganisationRoutes(orgId);
  const { orgRouteEntries, preferredRouteEntries } = useMemo(() => {
    const resolved = resolveOrganisationRoutes(
      orgRoutesQuery.data ?? [],
      crossingCatalogue.routeByKey,
    );
    return {
      orgRouteEntries: resolved.map((r) => r.entry),
      // Starred on Central Configuration — offered before anything else.
      preferredRouteEntries: resolved.filter((r) => r.row.isPreferred).map((r) => r.entry),
    };
  }, [crossingCatalogue.routeByKey, orgRoutesQuery.data]);
  const crossingPreference = organisationQuery.data?.crossingPreference ?? null;

  /**
   * The journey this order has to make, and where the waiting is. Null for a
   * single-stop order — nothing to plan — which is also why the panel below is
   * conditional rather than showing an empty frame.
   *
   * <p>
   * Built TWICE, and cheaply: the engine is pure, and only it knows which road
   * legs a journey of this shape has. The first pass reports those legs and
   * uses straight-line estimates; the second replaces them with real truck
   * driving times once they arrive. The alternative — the page working out
   * which legs to fetch — would duplicate the crossing/road-only branch and
   * then have to be kept in step with it.
   * </p>
   */
  const journeyInputs = useMemo(
    () => ({
      tasks: displayTasks,
      bookings: orderBookingsQuery.data ?? [],
      addresses: locationsQuery.data ?? [],
      orgRoutes: orgRouteEntries,
      preferredRoutes: preferredRouteEntries,
      crossingPreference,
      catalogueRoutes: crossingCatalogue.routes,
    }),
    [
      displayTasks,
      orderBookingsQuery.data,
      locationsQuery.data,
      orgRouteEntries,
      preferredRouteEntries,
      crossingPreference,
      crossingCatalogue.routes,
    ],
  );

  const journeyEstimate = useMemo(() => buildOrderJourney(journeyInputs), [journeyInputs]);

  // Rows whose planned window can't accommodate the position they sit
  // in. Advisory: the strip never reorders itself — see lib/taskOrder.ts.
  const timeConflictIds = useMemo(() => findChronologyConflicts(displayTasks), [displayTasks]);

  // Best-effort delivery territory for fuel-surcharge matching: the last task
  // with a country (collect → deliver, so the final leg is the delivery). Null
  // when unknown — the customer's flat ('All') fuel surcharge then applies.
  const deliveryCountryIso = useMemo<string | null>(() => {
    for (let i = displayTasks.length - 1; i >= 0; i--) {
      const c = displayTasks[i]?.countryCode;
      if (c) return c;
    }
    return null;
  }, [displayTasks]);

  // Once the order has at least one (persisted) task, the customer is locked and
  // the cargo may only be swapped for another of the SAME cargo type — changing
  // either would orphan the tasks and their cargo items.
  const lockHeader = displayTasks.length > 0;

  // Supplier map to resolve booking.carrierPartyId → display name on the chip.
  const supplierByPartyId = useMemo<Map<string, Party>>(() => {
    const map = new Map<string, Party>();
    for (const p of suppliersQuery.data ?? []) map.set(p.id, p);
    return map;
  }, [suppliersQuery.data]);

  const displayBookings = useMemo<MockBooking[]>(() => {
    return (orderBookingsQuery.data ?? []).map((b) =>
      adaptOrderBookingToMock(b, supplierByPartyId),
    );
  }, [orderBookingsQuery.data, supplierByPartyId]);

  const serviceTagsQuery = useServiceTags(orgId);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>('t1');
  const [isAddingTask, setIsAddingTask] = useState<boolean>(false);
  // Driver jobs (myTms) — opened from the Actions row or the Checkpoints panel.
  const [dispatchOpen, setDispatchOpen] = useState(false);
  // From the Checkpoints panel it opens straight on the send screen.
  const [dispatchView, setDispatchView] = useState<DispatchPanelView>('jobs');
  const openDispatch = (view: DispatchPanelView) => {
    setDispatchView(view);
    setDispatchOpen(true);
  };
  // Arriving from the driver-messages inbox ("Open order"): straight to the
  // conversations, then drop the flag so a refresh doesn't reopen them.
  const [routeParams, setRouteParams] = useSearchParams();
  useEffect(() => {
    if (routeParams.get('messages') !== '1') return;
    openDispatch('messages');
    const next = new URLSearchParams(routeParams);
    next.delete('messages');
    setRouteParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, when the flag arrives
  }, [routeParams]);

  // ── Task reordering ──────────────────────────────────────────────────
  // Sequence is the operational spine (the bookings cockpit reads its
  // ferry insertion point off it), so it only ever changes on an
  // explicit operator drag — never as a side effect of editing a date.
  const resequenceTasks = useResequenceOrderTasks();
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{ index: number; edge: 'above' | 'below' } | null>(
    null,
  );

  const clearDrag = useCallback(() => {
    setDragIndex(null);
    setDropTarget(null);
  }, []);

  const handleTaskDrop = useCallback(
    (targetIndex: number, edge: 'above' | 'below') => {
      const from = dragIndex;
      clearDrag();
      if (from === null || !id) return;

      // Drop edges are insertion points in the pre-move list; once the
      // source is spliced out, anything past it shifts down one.
      const insertAt = edge === 'above' ? targetIndex : targetIndex + 1;
      const to = insertAt > from ? insertAt - 1 : insertAt;
      if (to === from) return;

      const reordered = moveTask(displayTasks, from, to);
      resequenceTasks.mutate(
        { orderId: id, items: buildResequenceItems(reordered) },
        {
          onError: (err) =>
            showToast({ type: 'error', message: `Couldn't reorder tasks: ${err.message}` }),
        },
      );
    },
    [dragIndex, clearDrag, id, displayTasks, resequenceTasks],
  );

  // Pre-filled from the picked cargo def's serviceTagIds on cargo pick; operator may de-select.
  const [activeTags, setActiveTags] = useState<Set<string>>(() => new Set<string>());
  const [customerId, setCustomerId] = useState<string>('');
  const [cargoId, setCargoId] = useState<string>('');

  // Order number: minted via the org sequence at customer-pick time for new orders;
  // loaded from the BE for existing ones. Gaps in the sequence are the agreed behaviour
  // (better than retrying and risking duplicates).
  const [allocatedReference, setAllocatedReference] = useState<string>('');
  const [allocationError, setAllocationError] = useState<string | null>(null);
  const consumeSequence = useConsumeOrganisationSequence();
  const reference = allocatedReference || '(new)';
  // Guards against re-minting when the same customer is re-picked (e.g. stray extra click).
  const lastAllocatedForCustomer = useRef<string | null>(null);

  const allocateOrderNumber = (nextCustomerId: string) => {
    if (!isNew || !orgId || !nextCustomerId) return;
    if (lastAllocatedForCustomer.current === nextCustomerId) return;
    lastAllocatedForCustomer.current = nextCustomerId;
    setAllocationError(null);
    consumeSequence.mutate(
      { organisationId: orgId, type: 'Order' },
      {
        onSuccess: (res) => setAllocatedReference(res.formatted),
        onError: (err) => {
          setAllocatedReference('');
          // Surface BE messages verbatim — they already explain the
          // common failure ("No Order sequence configured for this
          // organisation."). Bail re-allocation gate so the operator
          // can retry by re-picking the same customer.
          lastAllocatedForCustomer.current = null;
          setAllocationError(err.message);
        },
      },
    );
  };

  const onCustomerChange = (next: string) => {
    setCustomerId(next);
    if (!next) {
      lastAllocatedForCustomer.current = null;
      setAllocatedReference('');
      setAllocationError(null);
      return;
    }
    allocateOrderNumber(next);
  };

  const customerOptions = useMemo(() => {
    const rows = customersQuery.data ?? [];
    return rows
      .filter((p) => p.status === 'Active')
      .map((p) => ({ value: p.id, label: p.name }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [customersQuery.data]);

  const pickedCustomer: Party | null = useMemo(() => {
    if (!customerId) return null;
    return customersQuery.data?.find((p) => p.id === customerId) ?? null;
  }, [customerId, customersQuery.data]);

  // Reference slots this customer insists on (Party → Required References).
  // Advisory: the panel marks them and warns when blank, nothing refuses the
  // save or the status move. Hard gates live in the workflow policy, which
  // applies to every customer alike.
  const requiredReferenceSlots = useMemo(
    () => parseRequiredOrderReferenceSlots(pickedCustomer?.tmsRequiredOrderReferencesJson),
    [pickedCustomer?.tmsRequiredOrderReferencesJson],
  );

  const cargoOptionRows = useMemo(() => {
    const defs = cargoDefinitionsQuery.data ?? [];
    const typeOrder = new Map<CargoTypeValue, number>(CARGO_TYPES.map((t, i) => [t, i]));

    interface Row {
      value: string;
      label: string;
      type: CargoTypeValue;
    }
    const rows: Row[] = [];
    for (const def of defs) {
      if (def.status !== 'Active') continue;
      if (VARIANT_EXPANDED_TYPES.has(def.cargoType) && def.variants.length > 0) {
        // One option per variant — combined label so two definitions
        // of the same type don't render as undistinguishable rows
        // (e.g. "Iso Tank · Iso Tank 40ft" vs "Iso Tank Reefer · Iso Tank 40ft").
        for (const variant of def.variants) {
          rows.push({
            value: buildCargoPick(def.id, variant.id),
            label: `${def.displayName} · ${variant.name}`,
            type: def.cargoType,
          });
        }
      } else {
        rows.push({
          value: buildCargoPick(def.id, null),
          label: def.displayName,
          type: def.cargoType,
        });
      }
    }
    return rows.sort((a, b) => {
      const ta = typeOrder.get(a.type) ?? 99;
      const tb = typeOrder.get(b.type) ?? 99;
      if (ta !== tb) return ta - tb;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
  }, [cargoDefinitionsQuery.data]);

  // Resolve the picked composite into the underlying CargoDefinition.
  // Downstream surfaces (task-type gate, service-tag pre-fill, cargo
  // items panel, …) read off this object rather than the raw composite.
  const pickedCargo: { definition: CargoDefinition; variantId: string | null } | null =
    useMemo(() => {
      const parsed = parseCargoPick(cargoId);
      if (!parsed) return null;
      const def = cargoDefinitionsQuery.data?.find((d) => d.id === parsed.definitionId);
      if (!def) return null;
      return { definition: def, variantId: parsed.variantId };
    }, [cargoId, cargoDefinitionsQuery.data]);

  const pickedCargoType: CargoTypeValue | null = pickedCargo?.definition.cargoType ?? null;
  // The pick as the cargo field shows it — "Iso Tank food quality · Iso Tank 40ft".
  const pickedCargoLabel = pickedCargo
    ? [
        pickedCargo.definition.displayName,
        pickedCargo.definition.variants.find((v) => v.id === pickedCargo.variantId)?.name,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  // Cargo combobox choices: all defs normally, but once the header is locked
  // (order has tasks) restrict to the same cargo type as the current pick.
  const cargoComboOptions = useMemo(
    () =>
      (lockHeader && pickedCargoType
        ? cargoOptionRows.filter((r) => r.type === pickedCargoType)
        : cargoOptionRows
      ).map(({ value, label }) => ({ value, label })),
    [cargoOptionRows, lockHeader, pickedCargoType],
  );
  // Pre-computed +Add Task button title — extracted to a local so TS
  // narrowing on `pickedCargoType` survives the JSX expression context
  // it's eventually consumed in (template literals inside JSX have
  // historically tripped narrowing in this file).
  const addTaskTitle =
    pickedCargoType !== null
      ? `Add task — ${(CARGO_TASK_TYPES[pickedCargoType] ?? []).length} types available for this cargo`
      : 'Pick a cargo first — the task types depend on it.';

  // Service tags shown on this order = every Active tag whose
  // `applyToServiceTypes` includes `Orders` (or the all-areas null /
  // empty sentinel). Surfacing every Order-applicable tag — not just
  // the cargo def's — lets the operator add an ad-hoc tag for THIS
  // specific order even if it isn't part of the cargo's default set.
  // The picked cargo def's tag IDs drive the initial selection (see
  // the effect below) but not the visible list.
  const availableServiceTags: ServiceTag[] = useMemo(() => {
    const tags = serviceTagsQuery.data ?? [];
    return tags
      .filter((t) => {
        if (t.status !== 'Active') return false;
        const raw = t.applyToServiceTypes?.trim();
        if (!raw) return true; // null/empty = applies to every area
        return raw
          .split(',')
          .map((s) => s.trim())
          .includes('Orders');
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [serviceTagsQuery.data]);

  /**
   * The vehicle to route as. An order being planned rarely has a unit assigned
   * yet, so the server fills in a standard artic — the one thing worth sending
   * is whether the load is hazardous, since ADR changes which tunnels and urban
   * roads are legal and therefore how long the leg takes.
   */
  const routingVehicle = useMemo(() => {
    const hazardous = availableServiceTags.some(
      (tag) =>
        activeTags.has(tag.id) &&
        // Matched on the tag's NAME and description: a service tag has no
        // code, and "ADR", "Haz" and "Hazardous goods" are the same fact to
        // a router — an org names them however it likes.
        /\b(adr|haz|hazmat|hazardous|dangerous)\b/i.test(`${tag.name} ${tag.description ?? ''}`),
    );
    return { hazardous };
  }, [availableServiceTags, activeTags]);

  const roadLegsQuery = useRoadLegs(orgId, journeyEstimate?.legPairs ?? [], routingVehicle);

  // The order's CO2, worked out once on the server and stored. Keyed on when
  // the order, its tasks and its bookings were last fetched: a save refetches
  // them, and the server replans only if the journey really changed.
  const emissionsQuery = useOrderEmissions(
    isNew ? null : (id ?? null),
    `${existingOrderQuery.dataUpdatedAt}:${orderTasksQuery.dataUpdatedAt}:${orderBookingsQuery.dataUpdatedAt}`,
  );

  const journey = useMemo(() => {
    if (!journeyEstimate) return null;
    const routed = roadLegsQuery.data;
    // Nothing back yet, or nothing routable: the estimate stands, and it says
    // so with a "~" on every leg it guessed.
    if (!routed?.some((leg) => leg.minutes != null)) return journeyEstimate;
    return buildOrderJourney({
      ...journeyInputs,
      resolveRoad: makeRoadResolver(journeyEstimate.legPairs, routed),
    });
  }, [journeyEstimate, journeyInputs, roadLegsQuery.data]);

  /** Tasks still carrying a reading — what the clear offer would affect. */
  const tasksWithTemperature = useMemo(
    () =>
      (orderTasksQuery.data ?? []).filter((t) => t.state !== 'Cancelled' && t.temperatureC != null),
    [orderTasksQuery.data],
  );

  // Which cargo pick the clear question has already been asked for, so changing
  // cargo twice does not re-ask about the same one.
  const [tempClearAskedFor, setTempClearAskedFor] = useState<string | null>(null);
  const [tempClearOpen, setTempClearOpen] = useState(false);

  // Inline on user event (not useEffect) so background refetches of serviceTagsQuery
  // can't clobber the hydrated tag set — a previous useEffect approach caused
  // "I tick haz, save, reopen, and haz is gone" because TQ re-fires on focus.
  const handleCargoPick = (next: string) => {
    setCargoId(next);
    if (!next) {
      setActiveTags(new Set<string>());
      setCargoOrderData(emptyCargoOrderData);
      return;
    }
    const parsed = parseCargoPick(next);
    const def = parsed
      ? (cargoDefinitionsQuery.data?.find((d) => d.id === parsed.definitionId) ?? null)
      : null;
    if (def) {
      const visibleIds = new Set(availableServiceTags.map((t) => t.id));
      const preSelected = def.serviceTagIds.filter((id) => visibleIds.has(id));
      setActiveTags(new Set(preSelected));
    } else {
      setActiveTags(new Set<string>());
    }
    setCargoOrderData(emptyCargoOrderData);

    // If the new cargo is not temperature-controlled, any task reading is now
    // orphaned: the Temp box stops rendering, so nobody would ever see it again,
    // yet it would still print on a manifest. Offer to clear rather than doing
    // it silently — the operator typed those values.
    //
    // Read from `def` directly: `pickedCargoPropertyGroups` is a memo over the
    // cargo state we have only just set, so it still describes the OLD cargo on
    // this tick.
    const nextGroups = def ? parsePropertyGroups(def.propertyGroupsJson) : null;
    const nextIsTemperatureControlled = nextGroups?.temperature !== undefined;
    if (
      !nextIsTemperatureControlled &&
      tasksWithTemperature.length > 0 &&
      next !== tempClearAskedFor
    ) {
      setTempClearOpen(true);
    }
  };

  // Per-order cargo values. temperatureC is the only live field; the shape
  // is kept open-ended so future fields (loading metres, etc.) slot in cleanly.
  interface CargoOrderData {
    temperatureC: number | null;
  }
  const emptyCargoOrderData: CargoOrderData = { temperatureC: null };
  const [cargoOrderData, setCargoOrderData] = useState<CargoOrderData>(emptyCargoOrderData);

  // Reference slots (index 0 = Reference1, …) + free-form extras.
  const [referenceValues, setReferenceValues] = useState<string[]>([]);
  const [referenceExtras, setReferenceExtras] = useState<OrderReferenceExtra[]>([]);

  const pickedCargoPropertyGroups = useMemo(
    () => (pickedCargo ? parsePropertyGroups(pickedCargo.definition.propertyGroupsJson) : null),
    [pickedCargo],
  );

  const patchCargoOrderData = <K extends keyof CargoOrderData>(key: K, next: CargoOrderData[K]) =>
    setCargoOrderData((prev) => ({ ...prev, [key]: next }));

  // ── Cascading the order temperature onto its tasks ───────────────────────
  //
  // A temperature-controlled order almost always runs at one set point, so a new
  // task defaults to the order's value and a CHANGE to the order's value offers
  // to push itself down. Offers, rather than does: a leg may legitimately differ
  // (a chilled trunk feeding a frozen store), and silently rewriting a value an
  // operator typed by hand is how trust in a screen goes.
  //
  // Keyed off blur, not change. Typing "10" fires onChange for "1" first, so a
  // prompt on change would ask about a temperature nobody meant.
  const updateTaskMutation = useUpdateOrderTask();
  const [tempCascadeAsked, setTempCascadeAsked] = useState<number | null>(null);
  const [tempCascadeOpen, setTempCascadeOpen] = useState(false);

  /** Tasks whose set point differs from the order's — the ones worth changing. */
  const tasksNeedingTemp = useMemo(() => {
    const target = cargoOrderData.temperatureC;
    if (target == null) return [];
    return (orderTasksQuery.data ?? []).filter(
      // Cancelled legs are history; rewriting their target teaches nothing.
      (t) => t.state !== 'Cancelled' && (t.temperatureC ?? null) !== target,
    );
  }, [cargoOrderData.temperatureC, orderTasksQuery.data]);

  const maybeOfferTempCascade = () => {
    const target = cargoOrderData.temperatureC;
    if (target == null) return;
    // Only once per value: re-asking every time focus leaves the box after the
    // operator already said no is nagging.
    if (tempCascadeAsked === target) return;
    if (tasksNeedingTemp.length === 0) return;
    setTempCascadeOpen(true);
  };

  /**
   * Write one temperature onto a set of tasks. `null` clears — used when the
   * cargo stops being temperature-controlled, where a leftover reading would be
   * invisible on screen (the box no longer renders) but would still travel on
   * manifests and documents.
   */
  const applyTemperatureToTasks = async (target: number | null, tasks: OrderTask[]) => {
    if (!id || tasks.length === 0) return;
    try {
      // Sequential rather than parallel: each is a whole-object PUT on the same
      // order, and the list query invalidates on every success.
      for (const task of tasks) {
        await updateTaskMutation.mutateAsync({
          orderId: id,
          body: {
            taskId: task.id,
            // Everything else is echoed back unchanged — this is a whole-object
            // PUT, so an omitted field would be nulled. Same care as
            // PickupDeliveryForm's save.
            sequence: task.sequence,
            addressId: task.addressId,
            state: task.state,
            taskReference: task.taskReference ?? null,
            notes: task.notes ?? null,
            plannedDate: task.plannedDate ?? null,
            plannedTime: task.plannedTime ?? null,
            plannedDepartureTime: task.plannedDepartureTime ?? null,
            timeMode: task.timeMode ?? null,
            actualDate: task.actualDate ?? null,
            actualArrivalTime: task.actualArrivalTime ?? null,
            actualDepartureTime: task.actualDepartureTime ?? null,
            statusText: task.statusText ?? null,
            podStatus: task.podStatus ?? null,
            podSignedAt: task.podSignedAt ?? null,
            podSignedBy: task.podSignedBy ?? null,
            serviceTagIds: task.serviceTagIds,
            temperatureC: target,
          },
        });
      }
      const n = tasks.length;
      showToast({
        type: 'success',
        message:
          target == null
            ? n === 1
              ? 'Task temperature cleared.'
              : `${n} task temperatures cleared.`
            : n === 1
              ? `Task temperature set to ${target}°C.`
              : `${n} task temperatures set to ${target}°C.`,
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Could not update the task temperatures.',
      });
    }
  };

  // Gates on cargoDefinitionsQuery.data so the composite cargoId key exists in the
  // dropdown options on first render. hasHydrated prevents cache refetches from
  // stomping operator edits on a single-edit screen.
  const hasHydrated = useRef(false);
  useEffect(() => {
    if (isNew) return;
    if (hasHydrated.current) return;
    const order = existingOrderQuery.data;
    if (!order) return;
    if (!cargoDefinitionsQuery.data) return;
    hasHydrated.current = true;

    setCustomerId(order.customerPartyId);
    setCargoId(buildCargoPick(order.cargoDefinitionId, order.cargoDefinitionVariantId ?? null));
    setAllocatedReference(order.orderNumber);
    setCargoOrderData({
      temperatureC: order.temperatureC ?? null,
    });
    setActiveTags(new Set(order.serviceTagIds));
  }, [isNew, existingOrderQuery.data, cargoDefinitionsQuery.data]);

  // Separate from the main hydration so the org query (needed for label count)
  // doesn't block customer/cargo/temp from landing.
  const referencesHydrated = useRef(false);
  useEffect(() => {
    if (isNew) return;
    if (referencesHydrated.current) return;
    const order = existingOrderQuery.data;
    if (!order) return;
    if (!organisationQuery.data) return;
    referencesHydrated.current = true;
    setReferenceValues(extractOrderReferenceValues(order, orderReferenceLabels.length));
    setReferenceExtras(extractOrderReferenceExtras(order));
  }, [isNew, existingOrderQuery.data, organisationQuery.data, orderReferenceLabels.length]);

  // Grow-only sync: never shrink so in-flight edits aren't lost on a refetch.
  useEffect(() => {
    setReferenceValues((prev) => {
      if (prev.length >= orderReferenceLabels.length) return prev;
      const next = prev.slice();
      while (next.length < orderReferenceLabels.length) next.push('');
      return next;
    });
  }, [orderReferenceLabels.length]);

  const showTemperature =
    pickedCargoPropertyGroups !== null && pickedCargoPropertyGroups.temperature !== undefined;

  const pickedVariant = useMemo(() => {
    if (!pickedCargo || !pickedCargo.variantId) return null;
    return pickedCargo.definition.variants.find((v) => v.id === pickedCargo.variantId) ?? null;
  }, [pickedCargo]);

  // Preset variants store physics (PerTrailer etc.) in the FE catalogue; custom
  // variants carry them on the row itself. This picks the right source.
  const pickedVariantPerTrailer = useMemo(() => {
    if (!pickedVariant || !pickedCargoType) return null;
    if (pickedVariant.presetKey) {
      const preset = findVariantPreset(pickedCargoType, pickedVariant.presetKey);
      return preset?.perTrailer ?? null;
    }
    return pickedVariant.perTrailer;
  }, [pickedVariant, pickedCargoType]);

  const isIsoOrContainer = pickedCargoType === 'IsoTank' || pickedCargoType === 'Container';
  const bicCount = isIsoOrContainer && pickedVariantPerTrailer === 2 ? 2 : 1;

  // Shared with the orders list, which reads the same families to decide what a
  // row's Info cell shows.
  const cargoFamily: CargoFamily = cargoFamilyFor(pickedCargoType);

  // Pallet types pickable on a pallet cargo-item row, sourced from the
  // picked definition's variants. Only the actual `Pallet` cargo type gets
  // a type picker — the 'pallet' *family* is a catch-all (FullLoad,
  // AgriGrains, Construction, …) whose rows keep the free-text Description
  // field. Preset variants resolve `loadingMetres` from the FE catalogue
  // (same source as `pickedVariantPerTrailer`); custom variants carry it
  // on the row. Empty for every non-Pallet cargo type.
  // What a NEW task inherits from the legs already on the order. Both are
  // empty on an order with no tasks — nothing to inherit, nothing to cascade.
  const inheritedTaskTemperatureC = useMemo(
    () => inheritedTemperatureC(displayTasks),
    [displayTasks],
  );
  const inheritedIdentifiers = useMemo(
    () => inheritedUnitIdentifiers(displayTasks, cargoFamily),
    [displayTasks, cargoFamily],
  );

  // "Start from a previous order" is shown by default; Stratum support can hide
  // it per organisation (Central Configuration → Copy Functions) — see
  // COPY_FUNCTION_DEFAULTS.
  const copyFunctions = parseCopyFunctions(organisationQuery.data?.tmsCopyFunctionsJson);
  const showOrdersCopySection = copyFunctions.ordersCopySection;

  // The currency the charges are valued in. Existing order's snapshot wins;
  // otherwise mirror the save logic — the selected customer's currency, then
  // the org default.
  const chargesCurrency =
    existingOrderQuery.data?.currencyIsoCode ??
    pickedCustomer?.currencyIsoCode ??
    organisationQuery.data?.currencyIsoCode ??
    'EUR';

  const palletVariants = useMemo<PalletVariantOption[]>(() => {
    if (pickedCargoType !== 'Pallet' || !pickedCargo) return [];
    return pickedCargo.definition.variants
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((v) => {
        const loadingMetres = v.presetKey
          ? (findVariantPreset('Pallet', v.presetKey)?.loadingMetres ?? v.loadingMetres)
          : v.loadingMetres;
        return { id: v.id, name: v.name, loadingMetres: loadingMetres ?? null };
      });
  }, [pickedCargo, pickedCargoType]);

  // The job as transport pricing sees it, for the Charges panel's rate-card
  // price. Rebuilt from the refetched tasks, so adding a task or editing a
  // pallet count re-prices it. Null until there's a saved order with a cargo.
  const transportJob = useMemo<TransportJob | null>(() => {
    // Null while the tasks are still loading, not an empty job: the panel
    // treats the first job it sees as "how the order opened", and a job that
    // opened empty and then filled in would read as the job having changed —
    // offering a re-price on every visit to an order with a special price.
    if (isNew || !pickedCargo || !orderTasksQuery.isSuccess) return null;
    return {
      cargoDefinitionId: pickedCargo.definition.id,
      isUnitCargo: cargoFamily === 'container' || cargoFamily === 'tank',
      unitCount: bicCount,
      currency: chargesCurrency,
      tasks: orderTasksQuery.data ?? [],
      palletVariants: palletVariants.map((v) => ({ id: v.id, name: v.name })),
      // Names only, so "why wasn't this priced?" can say which lane and cargo
      // it looked at.
      locationNames: Object.fromEntries([...addressById].map(([id, a]) => [id, a.name])),
      cargoNames: Object.fromEntries(
        (cargoDefinitionsQuery.data ?? []).map((d) => [d.id, d.displayName]),
      ),
    };
  }, [
    addressById,
    cargoDefinitionsQuery.data,
    isNew,
    pickedCargo,
    cargoFamily,
    bicCount,
    chargesCurrency,
    orderTasksQuery.isSuccess,
    orderTasksQuery.data,
    palletVariants,
  ]);

  // Temperature spec drilled into the task forms — only present when the
  // picked cargo type is temperature-controlled, mirroring the order-level
  // box's `showTemperature` gate. Null hides the per-task Temp box.
  const taskTemperatureSpec = useMemo<TaskTemperatureSpec | null>(() => {
    if (!showTemperature) return null;
    const t = pickedCargoPropertyGroups?.temperature;
    return {
      minTempC: t?.minTempC ?? null,
      maxTempC: t?.maxTempC ?? null,
      maxTransitDays: t?.maxTransitDays ?? null,
    };
  }, [showTemperature, pickedCargoPropertyGroups]);

  // Allocation pool: the order's collected cargo items, and how much of each
  // has already been delivered (across every delivery leg). The delivery
  // allocation table reads both to show remaining-to-deliver.
  const collectionItems = useMemo<CollectionItemOption[]>(() => {
    const out: CollectionItemOption[] = [];
    for (const t of orderTasksQuery.data ?? []) {
      if (cargoHandlingPhaseFor(t.type) !== 'load') continue;
      const a = addressById.get(t.addressId);
      const taskLabel = a ? (a.city ? `${a.name} — ${a.city}` : a.name) : 'Collection';
      for (const item of t.cargoItems ?? []) {
        // A unit carrying several products: each line is allocated on its own
        // (compartment 2 to customer B), by its own weight or volume.
        const lines = (item.products ?? []).slice().sort((a, b) => a.sequence - b.sequence);
        if (lines.length >= 2) {
          for (const line of lines) {
            const lineMeasure =
              line.weightKg != null && line.weightKg > 0
                ? 'kg'
                : line.volumeM3 != null && line.volumeM3 > 0
                  ? 'litres'
                  : null;
            if (!lineMeasure) continue;
            out.push({
              id: line.id,
              sourceItemId: item.id,
              productLineId: line.id,
              unitIndex: item.itemIndex,
              taskLabel,
              kind: item.kind,
              description: item.description ?? null,
              quantityUnit: null,
              measure: lineMeasure,
              collectedQty:
                lineMeasure === 'kg'
                  ? (line.weightKg ?? 0)
                  : Math.round((line.volumeM3 ?? 0) * 1000),
              productLabel: [item.identifier, `${line.sequence + 1}`, productLineLabel(line)]
                .filter(Boolean)
                .join(' · '),
              loadingMetresM: null,
              weightKg: line.weightKg,
              volumeM3: line.volumeM3,
            });
          }
          continue;
        }
        // Pallet lines by count; tank / bulk loads by weight or volume.
        const measure = allocationMeasureFor(item);
        if (!measure) continue;
        out.push({
          id: item.id,
          sourceItemId: item.id,
          productLineId: null,
          unitIndex: item.itemIndex,
          taskLabel,
          kind: item.kind,
          description: item.description ?? null,
          quantityUnit: item.quantityUnit ?? null,
          measure,
          collectedQty: collectedAmount(item, measure),
          productLabel: cargoItemProductLabel(item),
          loadingMetresM: item.loadingMetresM ?? null,
          weightKg: item.weightKg ?? null,
          volumeM3: item.volumeM3 ?? null,
        });
      }
    }
    return out;
  }, [orderTasksQuery.data, addressById]);

  const deliveredByCollectionItem = useMemo<Record<string, number>>(() => {
    // Summed in each SOURCE's measure — a delivery off a tank counts its
    // weight (or volume), off a pallet line its count.
    const measureBySource = new Map(collectionItems.map((c) => [c.id, c.measure]));
    const map: Record<string, number> = {};
    for (const t of orderTasksQuery.data ?? []) {
      if (cargoHandlingPhaseFor(t.type) !== 'delivery') continue;
      for (const item of t.cargoItems ?? []) {
        const key = allocationKeyOf(item);
        const measure = key ? measureBySource.get(key) : undefined;
        if (!key || !measure) continue;
        map[key] = (map[key] ?? 0) + deliveredAmount(item, measure);
      }
    }
    return map;
  }, [orderTasksQuery.data, collectionItems]);

  // Per-commodity balance for the order: how much of each cargo description has
  // been collected vs delivered (summed across all cargo-handling legs).
  const cargoBalance = useMemo(() => {
    const collected = new Map<string, number>();
    const delivered = new Map<string, number>();
    for (const t of orderTasksQuery.data ?? []) {
      if (!CARGO_HANDLING_TASK_TYPES.has(t.type)) continue;
      const bucket = cargoHandlingPhaseFor(t.type) === 'delivery' ? delivered : collected;
      for (const item of t.cargoItems ?? []) {
        if (item.quantity == null) continue;
        const key = item.description?.trim() || '—';
        bucket.set(key, (bucket.get(key) ?? 0) + item.quantity);
      }
    }
    return Array.from(collected.entries())
      .map(([commodity, c]) => ({
        commodity,
        collected: c,
        delivered: delivered.get(commodity) ?? 0,
      }))
      .sort((a, b) => a.commodity.localeCompare(b.commodity));
  }, [orderTasksQuery.data]);

  /**
   * POD documents on this order, as coverage over its delivery legs.
   *
   * <p>
   * The same query the Documents panel runs, so react-query serves both from
   * one fetch — and the strip cannot claim a POD is missing while the panel
   * next to it shows the file.
   * </p>
   */
  const orderDocumentsQuery = useOrderDocuments(isNew ? null : (id ?? null));
  const podCoverage = useMemo(
    () => podCoverageFrom(orderDocumentsQuery.data),
    [orderDocumentsQuery.data],
  );

  // Products the order's collection rows may carry: the cargo type's linked
  // products, or every active product when it links none. Null hides the
  // picker — products switched off, or this cargo type has no Products.
  const productsOn = organisationQuery.data?.useProducts ?? false;
  const cargoCarriesProducts = productsOn && (pickedCargo?.definition.hasProducts ?? false);
  const directoryProductsQuery = useDirectoryProducts(cargoCarriesProducts ? orgId : null, {
    status: 'Active',
  });
  const productOptions = useMemo(
    () =>
      buildCargoProductOptions(
        productsOn,
        pickedCargo?.definition ?? null,
        directoryProductsQuery.data,
      ),
    [productsOn, pickedCargo?.definition, directoryProductsQuery.data],
  );

  // Compartments on the cargo type: a unit may carry several products.
  const multiProduct = useMemo(
    () => cargoAllowsMultipleProducts(pickedCargo?.definition ?? null),
    [pickedCargo?.definition],
  );

  const taskPanelContext = useMemo<TaskPanelContext>(
    () => ({
      collectionItems,
      deliveredByCollectionItem,
      addresses: locationsQuery.data ?? [],
      serviceTagsCatalogue: availableServiceTags,
      orderActiveTagIds: Array.from(activeTags),
      cargoFamily,
      perTrailer: bicCount,
      palletVariants,
      temperatureSpec: taskTemperatureSpec,
      organisationId: orgId,
      siblingTasks: displayTasks,
      podCoverage,
      productOptions,
      multiProduct,
    }),
    [
      collectionItems,
      deliveredByCollectionItem,
      locationsQuery.data,
      availableServiceTags,
      activeTags,
      cargoFamily,
      bicCount,
      palletVariants,
      taskTemperatureSpec,
      orgId,
      displayTasks,
      podCoverage,
      productOptions,
      multiProduct,
    ],
  );

  const customerLabel = pickedCustomer?.name ?? 'Customer';
  // Where the job ends: the location of the last task by sequence. Null until
  // the order has a task (the subtitle then shows the customer alone).
  const lastTask = (orderTasksQuery.data ?? []).reduce<OrderTask | null>(
    (last, t) => (!last || t.sequence > last.sequence ? t : last),
    null,
  );
  const lastTaskLabel = lastTask ? (addressById.get(lastTask.addressId)?.name ?? null) : null;

  // ── Workflow: which move is available, and what stands in its way ─────────
  //
  // The organisation's saved policy laid over the shipped track, exactly as the
  // settings screen does it — so what an operator configured there is what
  // gates the button here. No policy row yet means the shipped defaults, which
  // is what `applyPolicy` leaves alone.
  const workflowPoliciesQuery = useWorkflowPolicies(orgId);
  const orderWorkflow = useMemo(() => {
    const track = WORKFLOW_GROUPS.flatMap((g) => g.tracks).find((t) => t.key === 'order');
    if (!track) return null;
    const policy = workflowPoliciesQuery.data?.find((p) => p.trackKey === 'order');
    return policy ? applyPolicy(track, policy.config) : track;
  }, [workflowPoliciesQuery.data]);

  // The order's charge lines, for the workflow facts (`charges`, and the
  // `invoiceId` stamps that answer "invoiced"). React Query dedupes this with
  // the charges panel's own fetch, so reading them here costs no request.
  const orderChargeCostsQuery = useOrderChargeCosts(isNew ? null : (id ?? null));

  // Invoices raised against THIS order, found through the charge lines that
  // carry an `invoiceId`. An uninvoiced order fetches nothing — which is most
  // of them, and all of the ones where the forward move matters.
  const invoiceIds = useMemo(() => {
    const ids = new Set<string>();
    for (const line of orderChargeCostsQuery.data ?? []) {
      if (line.invoiceId) ids.add(line.invoiceId);
    }
    return [...ids];
  }, [orderChargeCostsQuery.data]);

  const invoiceQueries = useQueries({
    queries: invoiceIds.map((id) => ({
      queryKey: ['tms', 'invoice', id] as const,
      queryFn: () => tmsApi.getInvoice(id),
      staleTime: 30 * 1000,
    })),
  });
  const orderInvoices = useMemo(
    () => invoiceQueries.map((q) => q.data).filter((i): i is Invoice => !!i),
    [invoiceQueries],
  );

  const workflowSubject = useMemo(() => {
    const existing = existingOrderQuery.data;
    if (!existing) return null;
    return toWorkflowSubject({
      order: existing,
      tasks: orderTasksQuery.data ?? [],
      charges: orderChargeCostsQuery.data ?? [],
      invoices: orderInvoices,
      paymentMethod: pickedCustomer?.paymentMethod,
      podCoverage,
      // The form's values, not the stored ones: the move saves them, so it
      // must not refuse over a reference the operator has already typed.
      references: referenceValues,
    });
  }, [
    existingOrderQuery.data,
    orderTasksQuery.data,
    orderChargeCostsQuery.data,
    orderInvoices,
    pickedCustomer?.paymentMethod,
    podCoverage,
    referenceValues,
  ]);

  const moves = useMemo<MoveAssessment[]>(() => {
    const existing = existingOrderQuery.data;
    if (!orderWorkflow || !workflowSubject || !existing) return [];
    return movesFrom(orderWorkflow, existing.status)
      .map((t) => assessMove(orderWorkflow, t, workflowSubject, orderReferenceLabels))
      .filter((m): m is MoveAssessment => m !== null);
  }, [orderWorkflow, workflowSubject, existingOrderQuery.data, orderReferenceLabels]);

  // Backwards moves — offered separately and only through the "⋮", because they
  // are corrections rather than progress.
  const correctionMoves = useMemo<MoveAssessment[]>(() => {
    const existing = existingOrderQuery.data;
    if (!orderWorkflow || !existing) return [];
    return correctionMovesFrom(orderWorkflow, existing.status);
  }, [orderWorkflow, existingOrderQuery.data]);

  const anyMutationPending =
    createOrderMutation.isPending ||
    updateOrderMutation.isPending ||
    replaceChargeCostsMutation.isPending;
  const canSave =
    !!orgId &&
    !!customerId &&
    !!allocatedReference &&
    !!pickedCargo &&
    !anyMutationPending &&
    (isNew || hasHydrated.current);

  const temperatureSpec = pickedCargoPropertyGroups?.temperature;
  const temperatureViolation =
    showTemperature &&
    cargoOrderData.temperatureC != null &&
    temperatureSpec?.minTempC != null &&
    temperatureSpec?.maxTempC != null &&
    (cargoOrderData.temperatureC < temperatureSpec.minTempC ||
      cargoOrderData.temperatureC > temperatureSpec.maxTempC)
      ? `Temperature ${cargoOrderData.temperatureC}°C is outside the allowed range ` +
        `(${temperatureSpec.minTempC}°C to ${temperatureSpec.maxTempC}°C) for this cargo.`
      : null;

  const handleSave = () => {
    if (!canSave || !orgId || !pickedCargo) return;
    if (temperatureViolation) {
      showToast({ type: 'error', message: temperatureViolation });
      return;
    }
    const referencePayload = toOrderReferenceSavePayload(referenceValues, referenceExtras);
    const cargoFields = {
      customerPartyId: customerId,
      cargoDefinitionId: pickedCargo.definition.id,
      cargoDefinitionVariantId: pickedCargo.variantId,
      // Snapshot perTrailer so a later catalogue edit can't change live orders' unit counts.
      perTrailer: pickedVariantPerTrailer ?? 1,
      temperatureC: showTemperature ? cargoOrderData.temperatureC : null,
      serviceTagIds: Array.from(activeTags),
      ...referencePayload,
    };

    const onError = (err: Error) =>
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save order.',
      });

    // Persist the charge/cost set alongside the order (same footer Save). The
    // panel reports its current lines into chargeCostsRef; we only fire once
    // it has hydrated so an early save can't blank a not-yet-loaded set.
    const persistChargeCosts = (targetOrderId: string) => {
      const snapshot = chargeCostsRef.current;
      if (!snapshot.ready) return;
      const lines = [
        ...snapshot.charges.map((l, i) => lineToInput(l, 'Charge', i)),
        ...snapshot.costs.map((l, i) => lineToInput(l, 'Cost', i)),
      ];
      replaceChargeCostsMutation.mutate(
        { orderId: targetOrderId, lines },
        {
          onError: (err) =>
            showToast({
              type: 'error',
              message:
                err instanceof Error
                  ? `Charges & costs: ${err.message}`
                  : 'Failed to save charges & costs.',
            }),
        },
      );
    };

    if (isNew) {
      createOrderMutation.mutate(
        {
          organisationId: orgId,
          orderNumber: allocatedReference,
          // Snapshot the currency at creation — the order's transactional
          // currency for charges/costs/invoicing. Prefer the selected
          // customer's currency; fall back to the org default.
          currencyIsoCode:
            pickedCustomer?.currencyIsoCode ?? organisationQuery.data?.currencyIsoCode ?? null,
          ...cargoFields,
        },
        {
          onSuccess: (order) => {
            showToast({ type: 'success', message: `Order ${order.orderNumber} saved.` });
            // Default charge lines are seeded server-side at creation — no FE
            // charge save here. Land on the freshly-saved row in edit mode.
            navigate(`/orders/${order.id}`);
          },
          onError,
        },
      );
      return;
    }

    // Edit flow — the order id is the URL param. The status field
    // isn't user-editable in v1 yet, so we echo the loaded value
    // back; the BE expects it on every PUT.
    const existing = existingOrderQuery.data;
    if (!existing) return;
    updateOrderMutation.mutate(
      {
        organisationId: orgId,
        body: {
          orderId: existing.id,
          status: existing.status,
          // Echoed: this page has no order-level notes field, and the PUT is a
          // replace — omitted, the column was cleared on every save.
          notes: existing.notes ?? null,
          ...cargoFields,
        },
      },
      {
        onSuccess: (order) => {
          showToast({ type: 'success', message: `Order ${order.orderNumber} updated.` });
          persistChargeCosts(existing.id);
        },
        onError,
      },
    );
  };

  /**
   * Apply a workflow move.
   *
   * <p>
   * Sends the SAME field set as Save, with the new status. The update handler
   * assigns every column from the command, so a status-only PUT would null the
   * cargo, references and service tags — the whole-object hazard this codebase
   * has been bitten by before. It also means moving the status saves the form,
   * which is the behaviour an operator expects from a button that commits.
   * </p>
   */
  const handleMove = async (move: MoveAssessment) => {
    const existing = existingOrderQuery.data;
    if (!existing || !orgId || !pickedCargo) return;

    const referencePayload = toOrderReferenceSavePayload(referenceValues, referenceExtras);
    try {
      const order = await updateOrderMutation.mutateAsync({
        organisationId: orgId,
        body: {
          orderId: existing.id,
          status: move.toStatus,
          // Same echo as the save above — a status move PUTs the whole order.
          notes: existing.notes ?? null,
          customerPartyId: customerId,
          cargoDefinitionId: pickedCargo.definition.id,
          cargoDefinitionVariantId: pickedCargo.variantId,
          perTrailer: pickedVariantPerTrailer ?? 1,
          temperatureC: showTemperature ? cargoOrderData.temperatureC : null,
          serviceTagIds: Array.from(activeTags),
          ...referencePayload,
        },
      });
      showToast({
        type: 'success',
        message: `${order.orderNumber} moved to ${move.toLabel}.`,
      });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to move the order.',
      });
    }
  };

  // One controller for both controls: they sit in different parts of the page
  // but share a confirmation dialog and a "which move is running" flag.
  const orderActions = useOrderActions({
    moves,
    corrections: correctionMoves,
    onMove: handleMove,
    busy: anyMutationPending,
    disabledReason: temperatureViolation
      ? 'Fix the temperature before moving this order.'
      : undefined,
  });

  const handleCancel = () => navigate('/orders');

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title={isNew ? 'New order' : `Edit order · ${reference}`}
        description={
          isNew
            ? 'Capture customer, cargo, and the sequence of tasks for this transport job.'
            : lastTaskLabel
              ? `${customerLabel} → ${lastTaskLabel}`
              : customerLabel
        }
      />

      <main className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto pb-20">
        {/* Customer header: Customer | Code/Currency/Payment | Order Number */}
        <section className="rounded-md border border-slate-200 bg-white">
          <div className="grid grid-cols-1 items-center gap-x-3 lg:grid-cols-4">
            <div className="relative px-3 py-2">
              <Combobox
                label="Customer *"
                value={customerId}
                onChange={onCustomerChange}
                options={customerOptions}
                disabled={lockHeader}
                placeholder={
                  customersQuery.isPending ? 'Loading customers…' : 'Start typing a customer name…'
                }
              />
              {isNew && customerId && allocationError && (
                <p className="mt-1 text-xs text-red-600">{allocationError}</p>
              )}
              {!isNew && existingOrderQuery.data && (
                <InvoiceToControl
                  orderId={existingOrderQuery.data.id}
                  organisationId={orgId}
                  customerPartyId={existingOrderQuery.data.customerPartyId}
                  invoiceToPartyId={existingOrderQuery.data.invoiceToPartyId ?? null}
                  options={customerOptions}
                />
              )}
              {isNew && customerId && consumeSequence.isPending && !allocationError && (
                <p className="mt-1 text-xs text-slate-500">Allocating order number…</p>
              )}
            </div>

            <div className="px-3 py-2">
              {pickedCustomer && (
                <div className="flex items-center gap-6">
                  <HeaderInfo label="Code" value={pickedCustomer.code} />
                  <HeaderInfo label="Currency" value={pickedCustomer.currencyIsoCode} />
                  <HeaderInfo label="Payment" value={pickedCustomer.paymentMethod} />
                </div>
              )}
            </div>

            {/* Status, alongside the identity rather than buried in the
                footer: it decides what every other control on the page is
                allowed to do, so it belongs where an operator looks first. */}
            <div className="px-3 py-2">
              {!isNew && existingOrderQuery.data && (
                <>
                  <p className={HEADER_LABEL}>Status</p>
                  <p className={HEADER_VALUE_ROW}>
                    {/* Same type as Code / Currency / Payment beside it. Only
                        the colour carries the state, so a cancelled order still
                        reads as one at a glance without a chip. */}
                    <span
                      className={`text-sm font-semibold leading-5 ${ORDER_STATUS_TEXT[existingOrderQuery.data.status]}`}
                    >
                      {ORDER_STATUS_LABEL[existingOrderQuery.data.status]}
                    </span>
                    <span className="text-[10px] italic leading-5 text-slate-400">
                      {fmtInstant(
                        existingOrderQuery.data.updatedUtc ?? existingOrderQuery.data.createdUtc,
                      )}
                    </span>
                  </p>
                </>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-3 py-2">
              <div>
                {allocatedReference && (
                  <>
                    <p className={HEADER_LABEL}>Order number</p>
                    <p className={HEADER_VALUE_ROW}>
                      <span className="text-lg font-bold leading-6 text-slate-900">{allocatedReference}</span>
                    </p>
                  </>
                )}
              </div>
              {/* Trip Checkpoints on its own, beside the order number — only
                  with the TMS Mobile module. */}
              <div className="flex shrink-0 items-center gap-2">
                {/* Driver messages: straight to the conversations. */}
                <OrderMessagesButton
                  orderId={id ?? null}
                  organisationId={orgId}
                  onOpen={() => openDispatch('messages')}
                />
                <OrderCheckpointsButton
                  orderId={id ?? null}
                  organisationId={orgId}
                  onSendToDriver={() => openDispatch('send')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Cargo header, on the same 4-column grid as the customer row above:
            Cargo | Temp | Service tags (spanning the last two, under Status). */}
        <section className="rounded-md border border-slate-200 bg-white">
          {/* items-start so the SERVICE TAGS eyebrow label stays level with the
              Cargo* floating label. FOUR columns, matching the customer row
              above: at three, the Cargo box was a third of the width against
              Customer's quarter and their right edges did not line up. */}
          <div className="grid grid-cols-1 items-start gap-x-3 lg:grid-cols-4">
            <div className="px-3 py-2">
              <Combobox
                label="Cargo *"
                value={cargoId}
                onChange={handleCargoPick}
                options={cargoComboOptions}
                placeholder={
                  cargoDefinitionsQuery.isPending
                    ? 'Loading cargo definitions…'
                    : cargoOptionRows.length === 0
                      ? 'No cargo definitions configured — add some in Central Configuration.'
                      : 'Start typing a cargo or variant name…'
                }
                allowClear={!lockHeader}
              />
            </div>

            {/* One column — the input and its spec hint ("Min: -20, Max: 20")
                share the quarter, which is what the field needs and no more. */}
            <div className="px-3 py-2">
              {pickedCargo && showTemperature && (
                <div className="space-y-3">
                  <InputWithSpecHint
                    spec={formatTempSpecRange(
                      pickedCargoPropertyGroups?.temperature?.minTempC,
                      pickedCargoPropertyGroups?.temperature?.maxTempC,
                      pickedCargoPropertyGroups?.temperature?.maxTransitDays,
                    )}
                  >
                    <NumberFieldInput
                      label="Temp (°C)"
                      value={cargoOrderData.temperatureC}
                      onChange={(n) => {
                        patchCargoOrderData('temperatureC', n);
                        // A fresh value deserves a fresh question.
                        if (n !== tempCascadeAsked) setTempCascadeAsked(null);
                      }}
                      onBlur={maybeOfferTempCascade}
                      allowNegative
                    />
                  </InputWithSpecHint>
                </div>
              )}
            </div>

            {/* Column 3, under STATUS. */}
            <div className="px-3 pb-3 pt-0">
              {cargoId && (
                <div>
                  <p className={HEADER_LABEL}>Service tags</p>
                  {availableServiceTags.length === 0 ? (
                    <p className={HEADER_VALUE_ROW}>
                      <span className="text-[11px] italic leading-5 text-slate-400">
                        No service tags configured for the Orders area.
                      </span>
                    </p>
                  ) : (
                    <div className={HEADER_VALUE_ROW}>
                      {availableServiceTags.map((tag) => {
                        const isActive = activeTags.has(tag.id);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => toggleTag(tag.id)}
                            title={tag.description ?? undefined}
                            className={
                              'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ' +
                              (isActive
                                ? 'border-amber-600 bg-amber-600 text-white'
                                : 'border-amber-300 bg-amber-50 text-amber-900 hover:border-amber-500')
                            }
                          >
                            {tag.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Column 4, under the order number: advancing the status, beside
                the status it changes. Labelled like its neighbours, and
                right-aligned to the edge the Checkpoints icon and the panels'
                "+ Add" share. Cancelling deliberately stays in the footer —
                abandoning an order is not the same gesture as progressing one
                and should not sit next to it. */}
            <div className="px-3 pb-3 pt-0">
              {/* History sits beside the move button rather than in a section of
                  its own: both are things you DO to the order, and a completed
                  order with no move left still has a history worth opening. */}
              {!isNew && (
                <div>
                  <p className={HEADER_LABEL}>Actions</p>
                  <div className={`${HEADER_VALUE_ROW} gap-x-1.5`}>
                    {/* History first: it is the one control here that changes
                        nothing, so it is the safe place for a cursor to land.
                        Then the forward move — a button, because it is what an
                        operator does most — and the "⋮" for the corrections. */}
                    <OrderHistoryButton
                      orderId={id ?? null}
                      organisationId={orgId}
                      orderNumber={existingOrderQuery.data?.orderNumber ?? null}
                      referenceLabels={orderReferenceLabels}
                    />
                    {/* Copy sits with History: same copy function and setting as
                        New order's "Start from a previous order". */}
                    {showOrdersCopySection && (
                      <CopyOrderButton
                        order={existingOrderQuery.data ?? null}
                        organisationId={orgId}
                      />
                    )}
                    {copyFunctions.ordersTemplates && (
                      <SaveAsTemplateButton
                        orderId={id ?? null}
                        orderNumber={existingOrderQuery.data?.orderNumber ?? null}
                        // "Carbery Group → Dublin Castle · Full Load"
                        suggestedName={[
                          lastTaskLabel ? `${customerLabel} → ${lastTaskLabel}` : customerLabel,
                          pickedCargoLabel,
                        ]
                          .filter(Boolean)
                          .join(' · ')
                          .slice(0, 128)}
                      />
                    )}
                    {/* Driver jobs: what has been sent to drivers' phones. */}
                    <DispatchButton
                      orderId={id ?? null}
                      organisationId={orgId}
                      onOpen={() => openDispatch('jobs')}
                    />
                    {orderActions.advance}
                    {orderActions.corrections}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          <ReferencesPanel
            labels={orderReferenceLabels}
            values={referenceValues}
            // Read off the customer the page already holds — the parties list is
            // loaded for the header combobox, so this costs no extra request and
            // follows a customer change immediately, before any save.
            requiredSlots={requiredReferenceSlots}
            onChange={(idx, next) =>
              setReferenceValues((prev) => {
                const out = prev.slice();
                // Pad to handle legacy data where slot count > array length.
                while (out.length <= idx) out.push('');
                out[idx] = next;
                return out;
              })
            }
            extras={referenceExtras}
            onExtrasChange={setReferenceExtras}
          />
          <DocumentsPanel orderId={isNew ? null : (id ?? null)} />
        </div>

        {/* Between the references and the tasks: the tasks each look fine on
            their own, so the waiting only becomes visible once the whole chain
            is added up. */}
        {journey && (
          <RouteInfoPanel
            plan={journey.plan}
            booked={journey.booked}
            emissions={emissionsQuery.data ?? null}
          />
        )}

        {/* Tasks and Charges & Costs only exist once the order does. Neither
            can be used before the first Save (tasks need an order id; the
            charge lines are seeded server-side at creation, and that Save
            never stores this panel's preview), and that Save lands on the
            saved order — where both appear. Showing them earlier was two
            empty boxes saying "save first". */}
        {/* New order, customer picked: their earlier orders to start from,
            and what would be copied from the one picked. */}
        {isNew && showOrdersCopySection && customerId && pickedCustomer && (
          <StartFromPreviousOrder
            organisationId={orgId}
            customerPartyId={customerId}
            customerName={pickedCustomer.name}
            cargoDefinitionId={pickedCargo?.definition.id ?? null}
            cargoVariantId={pickedCargo?.variantId ?? null}
            cargoLabel={pickedCargoLabel}
            newOrder={{
              orderNumber: allocatedReference,
              // As a plain save: the customer's currency, else the org default.
              currencyIsoCode:
                pickedCustomer.currencyIsoCode ?? organisationQuery.data?.currencyIsoCode ?? null,
              references: toOrderReferenceSavePayload(referenceValues, referenceExtras),
            }}
            templatesEnabled={copyFunctions.ordersTemplates}
          />
        )}

        {!isNew && (
        <>
        <section className="rounded-md border border-slate-200 bg-white">
          <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-3 py-2">
            <h2 className="text-sm font-semibold text-slate-900">
              Tasks <span className="text-slate-400">({displayTasks.length})</span>
            </h2>
            <button
              type="button"
              disabled={!pickedCargoType || isNew || isAddingTask}
              onClick={() => setIsAddingTask(true)}
              title={isNew ? 'Save the order first to add tasks.' : addTaskTitle}
              className="flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:cursor-not-allowed disabled:text-slate-400 disabled:hover:bg-transparent"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          </header>

          {/* Chronology warning — the task order is the operator's, so a
              row planned earlier than one above it is surfaced, never
              silently re-sorted. Either the order or the date is wrong
              and only the operator knows which. */}
          {timeConflictIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-3 py-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200">
                <AlertTriangle className="h-3 w-3" />
                {timeConflictIds.size === 1
                  ? '1 task is planned out of order'
                  : `${timeConflictIds.size} tasks are planned out of order`}
                <span className="mx-1 opacity-50">·</span>
                <span className="font-normal">
                  {displayTasks
                    .map((t, i) => (timeConflictIds.has(t.id) ? `#${i + 1} ${t.title}` : null))
                    .filter((s): s is string => s !== null)
                    .join(', ')}
                </span>
                <span className="mx-1 opacity-50">·</span>
                <span className="font-normal italic">drag to reorder, or fix the date</span>
              </span>
            </div>
          )}

          {/* Balance warning — only surfaces when a commodity's collected and
              delivered quantities DON'T reconcile (under- or over-delivered).
              Nothing shows when everything balances. */}
          {cargoBalance.some((b) => b.collected !== b.delivered) && (
            <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200 px-3 py-2">
              {cargoBalance
                .filter((b) => b.collected !== b.delivered)
                .map((b) => {
                  const toGo = b.collected - b.delivered;
                  const cls =
                    toGo < 0
                      ? 'bg-red-50 text-red-700 ring-red-200'
                      : 'bg-amber-50 text-amber-800 ring-amber-200';
                  const toGoLabel = toGo < 0 ? `${Math.abs(toGo)} over-delivered` : `${toGo} to go`;
                  return (
                    <span
                      key={b.commodity}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${cls}`}
                    >
                      <AlertTriangle className="h-3 w-3" />
                      <span className="font-semibold">{b.commodity}</span>
                      <span className="mx-1 opacity-50">·</span>
                      {b.collected} collected
                      <span className="mx-1 opacity-50">·</span>
                      {b.delivered} delivered
                      <span className="mx-1 opacity-50">·</span>
                      {toGoLabel}
                    </span>
                  );
                })}
            </div>
          )}

          {isAddingTask &&
            !isNew &&
            id &&
            pickedCargoType &&
            (() => {
              // The order's last task (highest sequence) seeds the new task's
              // default date/time — see TaskCreateForm's defaulting rules.
              const tasks = orderTasksQuery.data ?? [];
              const last = tasks.length
                ? tasks.reduce((a, b) => (a.sequence >= b.sequence ? a : b))
                : null;
              return (
                <TaskCreateForm
                  orderId={id}
                  orderNumber={existingOrderQuery.data?.orderNumber ?? null}
                  dangerousGoods={routingVehicle.hazardous}
                  equipmentKeys={pickedCargo?.definition.equipmentCatalogueKeys ?? []}
                  cargoType={pickedCargoType}
                  cargoFamily={cargoFamily}
                  palletVariants={palletVariants}
                  productOptions={productOptions}
                  multiProduct={multiProduct}
                  temperatureSpec={taskTemperatureSpec}
                  // Header value first — it's the authoritative spec. When
                  // it's blank but the legs carry readings, the newest leg
                  // supplies the default so each new stop doesn't start
                  // empty and get retyped.
                  defaultTemperatureC={cargoOrderData.temperatureC ?? inheritedTaskTemperatureC}
                  defaultUnitIdentifiers={inheritedIdentifiers}
                  perTrailer={bicCount}
                  addresses={locationsQuery.data ?? []}
                  organisationId={orgId}
                  serviceTagIds={Array.from(activeTags)}
                  serviceTagsCatalogue={availableServiceTags}
                  collectionItems={collectionItems}
                  deliveredByCollectionItem={deliveredByCollectionItem}
                  maxSequence={last?.sequence ?? 0}
                  previousPlannedDate={last?.plannedDate ?? null}
                  previousPlannedTime={
                    last?.timeMode === 'Exact' ? (last.plannedTime?.slice(0, 5) ?? null) : null
                  }
                  onSaved={() => setIsAddingTask(false)}
                  onCancel={() => setIsAddingTask(false)}
                />
              );
            })()}

          {!isNew && orderTasksQuery.isPending && (
            <div className="p-3">
              <Loading label="Loading tasks" />
            </div>
          )}
          {!isNew && orderTasksQuery.isError && (
            <div className="p-3">
              <ErrorBanner title="Failed to load tasks" message={orderTasksQuery.error.message} />
            </div>
          )}
          {!isNew && orderTasksQuery.isSuccess && displayTasks.length === 0 && (
            <p className="px-3 py-6 text-center text-xs italic text-slate-400">
              No tasks yet — click <span className="font-medium text-purple-600">+ Add</span> to
              plan the first leg.
            </p>
          )}

          {/* Two-zone task strip, consistent across families: the location group
              (#, type badge, title) takes the left ~1/3 via a wide flexible title
              track, and the data columns share the right ~2/3 evenly (each
              minmax(0,1fr)). The title fr is sized to D/2 (D = data-col count) so
              its share of the slack is exactly 1/3: container/tank 2.5fr over 5
              data cols (11 total), pallet 4fr over 8 (14 total). Subgrid aligns
              cells across cards; chevron pins to the last col via [grid-column:-2/-1]. */}
          {displayTasks.length > 0 && (
            <div className="overflow-x-auto">
              <ul
                className={
                  'grid gap-x-6 gap-y-2 px-3 py-2 ' +
                  (cargoFamily === 'pallet'
                    ? 'min-w-[72rem] grid-cols-[auto_auto_minmax(0,4fr)_auto_auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]'
                    : 'min-w-[68rem] grid-cols-[auto_auto_minmax(0,2.5fr)_auto_auto_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]')
                }
              >
                {displayTasks.map((task, index) => (
                  <li key={task.id} className="col-span-full grid grid-cols-subgrid">
                    <TaskCard
                      task={task}
                      index={index}
                      isOpen={selectedTaskId === task.id}
                      onToggle={() =>
                        setSelectedTaskId((cur) => (cur === task.id ? null : task.id))
                      }
                      bicCount={bicCount}
                      cargoFamily={cargoFamily}
                      bookings={displayBookings}
                      panelContext={taskPanelContext}
                      hasTimeConflict={timeConflictIds.has(task.id)}
                      // Reordering a single task is meaningless, and a
                      // second drag mid-write would race the optimistic
                      // update against the in-flight response.
                      drag={
                        displayTasks.length > 1 && !resequenceTasks.isPending
                          ? {
                              dropEdge: dropTarget?.index === index ? dropTarget.edge : null,
                              isSource: dragIndex === index,
                              onDragStart: () => setDragIndex(index),
                              onDragEnd: clearDrag,
                              onDragOverEdge: (edge) => setDropTarget({ index, edge }),
                              onDragLeave: () =>
                                setDropTarget((cur) => (cur?.index === index ? null : cur)),
                              onDrop: () => handleTaskDrop(index, dropTarget?.edge ?? 'above'),
                            }
                          : undefined
                      }
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <ChargesAndCostsPanel
          orderId={isNew ? null : (id ?? null)}
          organisationId={orgId}
          customerPartyId={customerId || null}
          deliveryCountryIso={deliveryCountryIso}
          currency={chargesCurrency}
          transportJob={transportJob}
          onChange={handleChargeCostsChange}
          orderPayerPartyId={
            existingOrderQuery.data
              ? (existingOrderQuery.data.invoiceToPartyId ??
                existingOrderQuery.data.customerPartyId)
              : null
          }
          payers={customerOptions}
        />
        </>
        )}
        <DispatchPanel
          open={dispatchOpen}
          startView={dispatchView}
          onClose={() => setDispatchOpen(false)}
          organisationId={orgId}
          orderId={isNew ? null : (id ?? null)}
          orderNumber={existingOrderQuery.data?.orderNumber ?? null}
        />
      </main>

      <footer className="sticky bottom-0 -mx-2 flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3">
        {/* Cancelling keeps the slot the delete control had, and runs through
            the same workflow assessment as any other move — a cancel that
            skipped the rules would make the rules optional. */}
        {!isNew && orderActions.cancel ? orderActions.cancel : <span />}

        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label="Cancel"
            onClick={handleCancel}
            className="flex h-8 w-8 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Save"
            onClick={handleSave}
            disabled={!canSave}
            className="flex h-8 w-8 items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Check className="h-4 w-4" />
          </button>
        </div>
      </footer>

      {orderActions.dialog}

      {/* Offering the new set point to the existing tasks. Named counts rather
          than "all tasks": an operator should know how many rows are about to
          change before agreeing to it. */}
      <ConfirmDialog
        open={tempCascadeOpen}
        title={`Set task temperature to ${cargoOrderData.temperatureC}°C?`}
        message={
          tasksNeedingTemp.length === 1
            ? `1 task has a different temperature. Set it to ${cargoOrderData.temperatureC}°C as well?`
            : `${tasksNeedingTemp.length} tasks have a different temperature. Set them all to ${cargoOrderData.temperatureC}°C?`
        }
        confirmLabel={
          tasksNeedingTemp.length === 1 ? 'Set the task' : `Set ${tasksNeedingTemp.length} tasks`
        }
        cancelLabel="Leave them"
        busy={updateTaskMutation.isPending}
        onConfirm={() => {
          setTempCascadeAsked(cargoOrderData.temperatureC);
          setTempCascadeOpen(false);
          void applyTemperatureToTasks(cargoOrderData.temperatureC, tasksNeedingTemp);
        }}
        onCancel={() => {
          // Remember the refusal so leaving the box again does not re-ask for
          // the same value.
          setTempCascadeAsked(cargoOrderData.temperatureC);
          setTempCascadeOpen(false);
        }}
      />
      {/* The mirror of the cascade: cargo is no longer temperature-controlled,
          so the readings have nowhere to be seen or edited. */}
      <ConfirmDialog
        open={tempClearOpen}
        title="Clear the task temperatures?"
        message={
          tasksWithTemperature.length === 1
            ? 'This cargo type is not temperature-controlled. 1 task still carries a temperature, which will no longer be shown anywhere. Clear it?'
            : `This cargo type is not temperature-controlled. ${tasksWithTemperature.length} tasks still carry a temperature, which will no longer be shown anywhere. Clear them?`
        }
        confirmLabel={
          tasksWithTemperature.length === 1
            ? 'Clear the task'
            : `Clear ${tasksWithTemperature.length} tasks`
        }
        cancelLabel="Keep them"
        busy={updateTaskMutation.isPending}
        onConfirm={() => {
          setTempClearAskedFor(cargoId);
          setTempClearOpen(false);
          void applyTemperatureToTasks(null, tasksWithTemperature);
        }}
        onCancel={() => {
          setTempClearAskedFor(cargoId);
          setTempClearOpen(false);
        }}
      />
    </div>
  );
}
